/**
 * The career feature (M2 plan §6 M2-22, order `FEATURE_ORDER.career` = 150): career mode only. A thin DOM shell
 * over the headless flow (`src/career/flow.ts`) and runtime (`src/career/runtime.ts`):
 *
 * - `init`: builds the flow (M2 data, the player ship's derived stats, the space world and its stages, the career
 *   runtime and its UI store, the `m2` service) and replaces "Click to fly" with the title screen, clearing boot's
 *   loading panel from `#overlay`.
 * - `beforeTicks`: the M2 tool input into `space.input` (tool modes consume `target` and `cyclePrimary`).
 * - `afterTick`: events → commands, due times, the death rebuild, autosaves.
 * - `frame`: the deferred station and prop models start loading after the first frame; the UI snapshot
 *   republishes at most `snapshotHz` times a second.
 *
 * The per-frame hooks allocate nothing (D-014).
 */
import { isDeferredAsset, loadAppConfig } from '../../app/config'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { createCareerFlow, flowBeforeTicks, flowFrame, type CareerFlow } from '../../career/flow'
import { hideOverlay } from '../../hud/overlays'
import { CAREER_ASSET_PREFIXES, CAREER_FLOW_TUNING, isCareerAsset } from './tuning'

let flow: CareerFlow | null = null

function careerBeforeTicks(ctx: GameContext, f: FrameInfo): void {
  if (flow !== null) flowBeforeTicks(flow.inputState, ctx.input, ctx.data.bindings, f.held === true)
}

function careerAfterTick(): void {
  if (flow !== null) flow.runtime.tick()
}

function careerFrame(f: FrameInfo): void {
  if (flow !== null) flowFrame(flow.frameState, f.now)
}

const feature: GameFeature = {
  name: 'career',
  order: FEATURE_ORDER.career,
  init(ctx: GameContext): void {
    if (ctx.mode !== 'career') return
    const config = loadAppConfig()
    flow = createCareerFlow(ctx, {
      isDeferred: (id) => isCareerAsset(CAREER_ASSET_PREFIXES, isDeferredAsset(config, id), id),
      tuning: CAREER_FLOW_TUNING,
      hideOverlay,
    })
  },
  beforeTicks(ctx: GameContext, f: FrameInfo): void {
    careerBeforeTicks(ctx, f)
  },
  afterTick(): void {
    careerAfterTick()
  },
  frame(_ctx: GameContext, f: FrameInfo): void {
    careerFrame(f)
  },
  dispose(): void {
    flow?.dispose()
    flow = null
  },
}

export default feature
